import { Alerter, Button, Callout, Dialog, FormGroup, Intent, ISelectOption, Select } from '@pinpointhq/thumbtack';
import * as React from 'react';
import { Company } from '../../../javascript/models';
import { createToast } from '../../FlashToaster';
import { useSpraypaintNamedParams } from '../shared/hooks/useSpraypaint';

export function MergeDialog({
  fetchData,
  company,
  handleClose,
}: {
  fetchData(): void;
  company: Company;
  handleClose(): void;
}) {
  const [selectedCompany, setSelectedCompany] = React.useState<string>();
  const { data: companies } = useSpraypaintNamedParams({
    initialScope: Company.per(5000).order({ name: 'asc' }),
  });

  const companyOptions = React.useMemo(() => {
    return (companies || [])
      .filter((c) => c.id !== company.id)
      .map((c) => ({
        label: c.name,
        value: c.id,
      }));
  }, [companies, company.id]);

  const selectedCompanyOption = React.useMemo(() => {
    return companyOptions.find((option) => option.value === selectedCompany) || null;
  }, [companyOptions, selectedCompany]);

  const handleSelect = (option: ISelectOption) => {
    if (option) {
      setSelectedCompany(option.value as string);
    } else {
      setSelectedCompany(null);
    }
  };

  const handleSubmit = () => {
    Alerter.create({
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      intent: Intent.DANGER,
      onConfirm: () => {
        company.mergingCompanyId = selectedCompany;
        company.save().then((success) => {
          if (success) {
            createToast({ text: 'Companies merged successfully', type: 'success' });
            fetchData();
            handleClose();
          } else {
            company.mergingCompanyId = null;
            const error = company.errors.base;
            createToast({ text: error ? error.message : 'Unable to merge these companies', type: 'error' });
          }
        });
      },
      text: 'Are you sure you want to merge these companies? This action cannot be undone.',
    });
  };

  return (
    <Dialog title="Merge company" onClose={handleClose} isOpen={true}>
      <Callout intent={Intent.WARNING} className="mar-b-2">
        When merging one company into another all associated data including internships, contacts and submissions will
        be transferred from one company to the other. If one of the companies has internships in the current cycle that
        will be the company that remains and we will remove the other one. If neither company has open internships in
        the current cycle then we will keep the company that was created first. If both companies have open internships
        in the current cycle the merge will fail.
      </Callout>

      <FormGroup label="Target company">
        <Select
          items={companyOptions}
          activeItem={selectedCompanyOption}
          onItemSelect={handleSelect}
          fill={true}
          buttonProps={{ large: true }}
        />
      </FormGroup>
      <Button text="Merge" fill={true} disabled={!selectedCompany} onClick={handleSubmit} />
    </Dialog>
  );
}
